import type { ReactNode } from "react";

import { useTranslation } from "react-i18next";
import { useMatches } from "react-router-dom";

export type CrumbFn = (
  data: unknown,
  params: Record<string, string | undefined>,
) => ReactNode;

export type CrumbHandle = {
  crumb?: CrumbFn;
};

export interface Crumb {
  key: string;
  pathname: string;
  node: ReactNode;
}

export function useCrumbs(): Crumb[] {
  // Read i18n.language so this hook re-runs on language change (the
  // `crumb()` closures call `i18n.t()` at render time, so they pick up the
  // new strings on the next render). Without this subscription, switching
  // language mid-session wouldn't refresh crumb text until the next nav.
  const { i18n } = useTranslation();
  void i18n.language;
  const matches = useMatches();
  return matches
    .filter((m) => (m.handle as CrumbHandle | undefined)?.crumb)
    .map((m) => ({
      key: m.id,
      pathname: m.pathname,
      node: (m.handle as CrumbHandle).crumb!(
        m.data,
        m.params as Record<string, string | undefined>,
      ),
    }));
}
